import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_HERO = "https://customer-assets.emergentagent.com/job_6ceb8fd6-1be7-4aed-8f48-c1411defaab2/artifacts/8jdfj3hu_image.png";

export default function ParentLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [identifier, setIdentifier] = useState(location.state?.identifier || location.state?.email || "");
  const [password, setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting]  = useState(false);

  // Org name lookup (cosmetic — shows org badge when identifier is recognized)
  const [orgInfo, setOrgInfo]   = useState({ loading: false, name: null, found: null });
  const lookupTimer             = useRef(null);

  // Forgot password
  const [showForgot, setShowForgot]             = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotResult, setForgotResult]         = useState(null); // null | "email" | "sms"

  // SMS OTP step
  const [otpMode, setOtpMode]         = useState(false);
  const [otpPhone, setOtpPhone]       = useState("");
  const [otpCode, setOtpCode]         = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);

  // ── Org name lookup (debounced 600ms) ──────────────────────────────────
  useEffect(() => {
    const val = identifier.trim();
    if (!val || (val.includes("@") ? !val.includes(".") : val.replace(/\D/g, "").length < 7)) {
      setOrgInfo({ loading: false, name: null, found: null });
      return;
    }
    setOrgInfo((prev) => ({ ...prev, loading: true }));
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/parent-org-lookup`, { params: { email: val } });
        setOrgInfo({ loading: false, name: data.organizationName, found: data.found });
      } catch {
        setOrgInfo({ loading: false, name: null, found: null });
      }
    }, 600);
    return () => clearTimeout(lookupTimer.current);
  }, [identifier]);

  // ── Login ───────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/parent-auth/login`, { email: identifier, password });
      localStorage.setItem("token", data.access_token);
      // Use full page reload so AuthContext re-reads token and fetches user
      window.location.href = "/parent";
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string'
        ? detail
        : (detail?.message || "Invalid credentials. Please check your email/phone and password.");

      if (detail?.otp_sent) {
        // Account locked — backend auto-sent an OTP; jump straight to OTP entry
        setOtpPhone(identifier.trim());
        setOtpMode(true);
        setShowForgot(true);
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Forgot password ─────────────────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/request-password-reset`, { email: forgotIdentifier });
      if (data.type === "sms") {
        // Phone OTP flow — show OTP entry
        setOtpPhone(forgotIdentifier);
        setOtpMode(true);
      } else {
        setForgotResult("email");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotSubmitting(false);
    }
  };

  // ── Verify SMS OTP ───────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-sms-otp`, { phone: otpPhone, otp: otpCode });
      navigate(`/reset-password/${data.reset_token}?return=/parent/login`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect code. Please try again.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  const resetForgot = () => {
    setShowForgot(false);
    setForgotResult(null);
    setOtpMode(false);
    setOtpCode("");
    setOtpPhone("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(249,115,22,0.12) 0%, transparent 40%), #f8fbfd" }}
      data-testid="parent-login-page"
    >
      <div className="mx-auto grid w-full max-w-5xl gap-8 lg:grid-cols-[1.1fr_0.9fr]">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden rounded-[2.2rem] shadow-2xl min-h-[420px]"
          data-testid="parent-login-hero"
        >
          <img
            src={DEFAULT_HERO}
            alt=""
            aria-hidden="true"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="parent-login-hero-image"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.38) 60%, rgba(0,0,0,0.18) 100%)" }} />
          <div className="relative space-y-6 px-8 py-12 text-white lg:px-10 lg:py-14">
            <img
              src="/simple-sign-in-logo.png"
              alt="ToDays Kidz logo"
              className="h-14 w-14 rounded-2xl bg-white/95 p-2 shadow-lg"
              data-testid="parent-login-logo"
            />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70" data-testid="parent-login-eyebrow">
                Parent Access
              </p>
              <h1 className="mt-2 text-4xl font-bold leading-tight sm:text-5xl" data-testid="parent-login-title">
                A family-first window into your child's day.
              </h1>
              <p className="mt-4 max-w-md text-sm text-white/80 sm:text-base" data-testid="parent-login-subtitle">
                View daily activity, messages, documents, and billing — all in one place.
              </p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-xs text-white/60">First time here?</p>
              <p className="mt-1 text-sm font-medium text-white/90">Check your email or text messages for an invite from your childcare center and click the link to create your account.</p>
            </div>
          </div>
        </section>

        {/* ── Form card ──────────────────────────────────────────────────── */}
        <Card className="border-0 bg-white/95 shadow-2xl ring-1 ring-slate-200/80 self-center" data-testid="parent-login-card">
          <CardContent className="p-6 sm:p-8">

            {!showForgot ? (
              /* ── Login form ── */
              <form onSubmit={handleLogin} className="space-y-5" data-testid="parent-login-form">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Parent Portal Login</h2>
                  <p className="mt-1 text-sm text-slate-500">Sign in to view your child's updates.</p>
                </div>

                {/* Org name badge */}
                {orgInfo.found === true && (
                  <div
                    className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200"
                    data-testid="parent-login-org-name"
                  >
                    <svg className="h-4 w-4 shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Logging into: <span className="font-semibold">{orgInfo.name}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="login-email">Email address or phone number</label>
                  <Input
                    id="login-email"
                    type="text"
                    autoComplete="username"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="name@example.com or 555-555-5555"
                    required
                    data-testid="parent-login-email"
                  />
                  <p className="text-xs text-slate-400">Use the email address or phone number you used when you activated your account.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="login-password">Password</label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                      data-testid="parent-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-testid="parent-login-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="parent-login-submit"
                >
                  {submitting ? "Signing in…" : "Sign in to Parent Portal"}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setForgotIdentifier(identifier); }}
                    className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline"
                    data-testid="parent-login-forgot-link"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>

            ) : (
              /* ── Forgot / OTP flow ── */
              <div className="space-y-5" data-testid="parent-forgot-password-section">

                {/* Step 1: Enter email or phone */}
                {!otpMode && !forgotResult && (
                  <>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Reset your password</h2>
                      <p className="mt-1 text-sm text-slate-500">Enter the email or phone number on your account.</p>
                    </div>
                    <form onSubmit={handleForgot} className="space-y-4" data-testid="parent-forgot-password-form">
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-slate-700" htmlFor="forgot-identifier">Email or phone number</label>
                        <Input
                          id="forgot-identifier"
                          type="text"
                          value={forgotIdentifier}
                          onChange={(e) => setForgotIdentifier(e.target.value)}
                          placeholder="name@example.com or 555-555-5555"
                          required
                          data-testid="parent-forgot-email-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={forgotSubmitting}
                        className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="parent-forgot-submit"
                      >
                        {forgotSubmitting ? "Sending…" : "Send reset instructions"}
                      </Button>
                      <div className="text-center">
                        <button type="button" onClick={resetForgot} className="text-sm text-slate-500 hover:text-slate-700 hover:underline" data-testid="parent-forgot-cancel">
                          Back to login
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {/* Step 2a: Email sent confirmation */}
                {forgotResult === "email" && (
                  <div className="space-y-4 text-center" data-testid="parent-forgot-password-success">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                      <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-slate-700">Check your inbox!</p>
                    <p className="text-sm text-slate-500">If <strong>{forgotIdentifier}</strong> matches an account, a reset link is on its way. Check your spam folder if you don't see it.</p>
                    <button type="button" onClick={resetForgot} className="text-sm text-emerald-600 hover:underline" data-testid="parent-forgot-back-to-login">
                      Back to login
                    </button>
                  </div>
                )}

                {/* Step 2b: SMS OTP entry */}
                {otpMode && (
                  <>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Enter your reset code</h2>
                      <p className="mt-1 text-sm text-slate-500">A 6-digit code was texted to <strong>{otpPhone}</strong>. Enter it below.</p>
                    </div>
                    <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="parent-otp-form">
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-slate-700" htmlFor="otp-code">6-digit code</label>
                        <Input
                          id="otp-code"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="123456"
                          required
                          data-testid="parent-otp-input"
                        />
                        <p className="text-xs text-slate-400">Code expires in 10 minutes.</p>
                      </div>
                      <Button
                        type="submit"
                        disabled={otpSubmitting || otpCode.length !== 6}
                        className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="parent-otp-submit"
                      >
                        {otpSubmitting ? "Verifying…" : "Verify code"}
                      </Button>
                      <div className="flex justify-between text-sm">
                        <button type="button" onClick={() => { setOtpMode(false); setOtpCode(""); }} className="text-slate-500 hover:underline">
                          Try different number
                        </button>
                        <button type="button" onClick={resetForgot} className="text-slate-500 hover:underline" data-testid="parent-forgot-cancel">
                          Back to login
                        </button>
                      </div>
                    </form>
                  </>
                )}

              </div>
            )}

          </CardContent>
        </Card>

      </div>
    </div>
  );
}
